import { type DropdownDirection } from '@consigli/facade';
import { type ProjectParticipant, type SpeakerAssignScope } from '@consigli/types';
import { FC, RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiUser, FiUsers } from 'react-icons/fi';

import { ReassignPersonPopover, type ReassignResultItem } from './reassign-person-popover';

import type { IconType } from 'react-icons';

interface ChangeSpeakerPopoverProps {
  /** Display label of the segment being edited (used in the batch-scope option). */
  currentSpeakerLabel: string;
  /** Currently assigned MeetingUser id, if any — marked with a checkmark. */
  currentAssignedUserId?: string | null;
  /** Whether a batch reassignment is meaningful for this segment. */
  canApplyToAll: boolean;
  /**
   * Which batch scope the "apply to all" option sends: `all_same_name` for an
   * unassigned segment (group by pipeline label) or `all_same_user` for an
   * already-assigned one (group by the user it is currently attributed to).
   */
  batchScope: Extract<SpeakerAssignScope, 'all_same_name' | 'all_same_user'>;
  participants: ProjectParticipant[];
  isSaving: boolean;
  direction: DropdownDirection;
  anchorRef?: RefObject<HTMLElement | null>;
  onSave: (meetingUserId: string, scope: SpeakerAssignScope) => void;
  onClose: () => void;
}

interface ScopeOption {
  value: SpeakerAssignScope;
  Icon: IconType;
  title: string;
  description: string;
  disabled: boolean;
}

/**
 * Transcript speaker reassignment: a single participant, no custom names, plus a
 * "apply to all matching segments" scope choice. A thin wrapper over the shared
 * ReassignPersonPopover — the scope radios are injected via its `renderExtra` slot.
 */
export const ChangeSpeakerPopover: FC<ChangeSpeakerPopoverProps> = ({
  currentSpeakerLabel,
  currentAssignedUserId,
  canApplyToAll,
  batchScope,
  participants,
  isSaving,
  direction,
  anchorRef,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();

  const [scope, setScope] = useState<SpeakerAssignScope>(canApplyToAll ? batchScope : 'single');

  const scopeOptions: ScopeOption[] = [
    {
      value: batchScope,
      Icon: FiUsers,
      title: t('meeting-secretary.meetings.apply-to-all-segments', { name: currentSpeakerLabel }),
      description: t('meeting-secretary.meetings.apply-to-all-segments-description'),
      disabled: isSaving || !canApplyToAll,
    },
    {
      value: 'single',
      Icon: FiUser,
      title: t('meeting-secretary.meetings.only-this-segment'),
      description: t('meeting-secretary.meetings.only-this-segment-description'),
      disabled: isSaving,
    },
  ];

  const handleSave = (result: ReassignResultItem[]) => {
    const meetingUserId = result[0]?.meetingUserId;
    if (!meetingUserId) return;
    onSave(meetingUserId, scope);
  };

  const scopeSection = (
    <div
      className="space-y-2 border-t border-gray-100 p-3"
      role="radiogroup"
      aria-label={t('meeting-secretary.meetings.change-speaker')}
    >
      {scopeOptions.map(({ value, Icon, title, description, disabled }) => {
        const checked = scope === value;
        return (
          <div
            key={value}
            role="radio"
            aria-checked={checked}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && setScope(value)}
            onKeyDown={(e) => {
              if (!disabled && (e.key === ' ' || e.key === 'Enter')) {
                e.preventDefault();
                setScope(value);
              }
            }}
            className={`flex gap-2.5 rounded-lg border p-2.5 ${
              checked ? 'border-brand bg-brand-light/40' : 'border-gray-200'
            } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          >
            <span
              aria-hidden
              className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border-2 border-brand ${
                checked ? 'border-[5px]' : ''
              }`}
            />
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                <Icon size={14} className="flex-shrink-0 text-gray-500" />
                {title}
              </p>
              <p className="text-xs text-gray-500">{description}</p>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <ReassignPersonPopover
      title={t('meeting-secretary.meetings.change-speaker')}
      participants={participants}
      mode="single"
      allowCustom={false}
      allowClear={false}
      selected={
        currentAssignedUserId
          ? [{ meetingUserId: currentAssignedUserId, name: currentSpeakerLabel }]
          : []
      }
      isSaving={isSaving}
      direction={direction}
      anchorRef={anchorRef}
      onSave={handleSave}
      onClose={onClose}
      renderExtra={scopeSection}
    />
  );
};
